import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const resolverRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: "Confirmer",
    message: "",
    confirmText: "Confirmer",
    cancelText: "Annuler",
    danger: false,
  });

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;

      setOpts((prev) => ({
        ...prev,
        title: options.title ?? prev.title,
        message: options.message ?? "",
        confirmText: options.confirmText ?? "Confirmer",
        cancelText: options.cancelText ?? "Annuler",
        danger: !!options.danger,
      }));

      setOpen(true);
    });
  }, []);

  const close = useCallback((result) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  }, []);

  const api = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}

      {open && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center px-4">
          {/* overlay */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => close(false)}
          />

          {/* modal */}
          <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">{opts.title}</h3>
                {opts.message ? (
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{opts.message}</p>
                ) : null}
              </div>

              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-xl px-2 py-1 text-sm text-slate-400 hover:bg-slate-100"
                aria-label="Fermer"
                title="Fermer"
              >
                ✕
              </button>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-2xl px-4 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                {opts.cancelText}
              </button>

              <button
                type="button"
                onClick={() => close(true)}
                className={
                  "rounded-2xl px-4 py-2 text-sm font-semibold text-white shadow-sm " +
                  (opts.danger
                    ? "bg-rose-600 hover:bg-rose-500"
                    : "bg-indigo-600 hover:bg-indigo-500")
                }
              >
                {opts.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within <ConfirmProvider />");
  }
  return ctx.confirm;
}
